import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { BookOpen } from "lucide-react";

interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  image_url: string;
  thumbnail_url: string | null;
  published_at: string;
}

const BlogPostsSection = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const fetchPosts = async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("slug, title, excerpt, image_url, thumbnail_url, published_at")
        .lte("published_at", new Date().toISOString())
        .order("published_at", { ascending: false })
        .limit(3);


      if (!error && data && data.length > 0) {
        setPosts(data as BlogPost[]);
      }
      setLoaded(true);
    };

    fetchPosts();
  }, []);

  // Ne rien afficher tant qu'on n'a pas chargé, ou si aucun article
  if (!loaded || posts.length === 0) {
    return null;
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  return (
    <section className="py-20 px-4 bg-background">
      <div className="max-w-6xl mx-auto space-y-10">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10">
            <BookOpen className="w-6 h-6 text-primary" />
          </div>
          <h2 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
            Derniers articles du collectif
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {posts.map((post, index) => (
            <Link
              key={post.slug}
              to={`/blog/${post.slug}`}
              className="group block"
              aria-label={post.title}
            >
              <Card className="border-0 shadow-md hover:shadow-lg transition-all duration-300 h-full overflow-hidden animate-fade-in">
                <CardContent className="p-0 flex flex-col h-full">
                  <div className="relative aspect-[16/9] overflow-hidden bg-muted">
                    <img
                      src={post.thumbnail_url || post.image_url}
                      alt={post.title}
                      loading="lazy"
                      width={400}
                      height={225}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5 space-y-3 flex flex-col flex-1">
                    <h3 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2 flex-1">
                      {post.excerpt}
                    </p>
                    <time
                      dateTime={post.published_at}
                      className="text-xs text-muted-foreground/70 uppercase tracking-wider"
                    >
                      {formatDate(post.published_at)}
                    </time>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        <div className="text-center pt-4">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-medium transition-colors"
          >
            Voir tous les articles
            <span aria-hidden="true">&rarr;</span>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default BlogPostsSection;
